import { useState } from 'react';
import { Calendar, Users } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '@/components/ux/PageHeader';
import EmptyState from '@/components/ux/EmptyState';
import StatusBadge from '@/components/ux/StatusBadge';
import MetricCard from '@/components/ux/MetricCard';
import CopyButton from '@/components/ux/CopyButton';
import ConfirmDialog from '@/components/ux/ConfirmDialog';
import ThemeToggle from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { BRAND } from '@/theme/theme.config.js';

/**
 * Internal design system preview — visit /design-preview
 */
export default function DesignPreviewPage() {
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <div className="eh-page py-8 sm:py-10">
      <PageHeader
        title="EchoHire Design System"
        description={`Brand ${BRAND} · shadcn/ui + UX kit · light/dark · responsive`}
        actions={
          <>
            <ThemeToggle />
            <Button variant="outline" onClick={() => toast.info('Sample toast')}>
              Toast
            </Button>
          </>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard title="Interviews" value="128" hint="This month" icon={Calendar} trend="+12%" />
        <MetricCard title="Candidates" value="56" hint="Active" icon={Users} />
        <MetricCard title="Completion" value="94%" hint="Avg. rate" />
        <MetricCard title="Avg. score" value="7.8" hint="Out of 10" />
      </div>

      <Separator className="my-8" />

      <Tabs defaultValue="components" className="w-full">
        <TabsList className="mb-6 flex h-auto w-full flex-wrap gap-1">
          <TabsTrigger value="components">Components</TabsTrigger>
          <TabsTrigger value="table">Table</TabsTrigger>
          <TabsTrigger value="states">States</TabsTrigger>
        </TabsList>

        <TabsContent value="components" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Buttons & badges</CardTitle>
              <CardDescription>Primary brand uses #009999</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              <Button>Primary</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="destructive">Destructive</Button>
              <Badge>Default</Badge>
              <Badge variant="success">Success</Badge>
              <Badge variant="warning">Warning</Badge>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Form</CardTitle>
            </CardHeader>
            <CardContent className="grid max-w-md gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" placeholder="you@company.com" />
              </div>
              <CopyButton value="https://echohire.app/interview/demo-link" label="Copy link" />
            </CardContent>
          </Card>

          <div className="flex flex-wrap gap-2">
            <StatusBadge status="scheduled" />
            <StatusBadge status="in_progress" />
            <StatusBadge status="completed" />
            <StatusBadge status="rejected" />
          </div>
        </TabsContent>

        <TabsContent value="table">
          <Card>
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Candidate</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="hidden sm:table-cell">Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Alex Chen</TableCell>
                    <TableCell>Frontend Engineer</TableCell>
                    <TableCell className="hidden sm:table-cell">May 16, 2026</TableCell>
                    <TableCell>
                      <StatusBadge status="completed" />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Sam Rivera</TableCell>
                    <TableCell>Product Manager</TableCell>
                    <TableCell className="hidden sm:table-cell">May 17, 2026</TableCell>
                    <TableCell>
                      <StatusBadge status="scheduled" />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="states" className="space-y-6">
          <EmptyState
            icon={Calendar}
            title="No interviews yet"
            description="Schedule your first AI interview to see candidates here."
            actionLabel="Schedule interview"
            onAction={() => toast.success('Action clicked')}
          />
          <div className="grid gap-3 sm:grid-cols-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
          <Button variant="destructive" onClick={() => setConfirmOpen(true)}>
            Open confirm dialog
          </Button>
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete template?"
        description="This action cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => {
          setConfirmOpen(false);
          toast.success('Confirmed');
        }}
      />
    </div>
  );
}

